import React from 'react';

const fmtPct = (v) => (v == null ? '—' : `${(Number(v) * 100).toFixed(1)}%`);

// The tile icon set and styling were refreshed for the September-2026 edition.
// Reports for earlier months keep the original house line icons and sizing so
// already-published periods re-render exactly as they did. From 2026-09 the
// container also carries `ip-mir-v2`, which styles.css uses for the lighter,
// larger Word-style look (grey icons, light labels, regular-weight values).
const NEW_STYLE_FROM = '2026-09';

// ---- Original icons (editions up to 2026-08): 24x24 grid, stroked ----------
// stroke = currentColor so they theme with the brand colour and render crisply
// in the WYSIWYG PDF.
const strokeSvg = (children) => (
  <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor"
    strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">{children}</svg>
);

const LEGACY_ICONS = {
  'Global Equities': strokeSvg(<>
    <circle cx="12" cy="12" r="9" />
    <path d="M3 12h18" />
    <path d="M12 3c3 3 3 15 0 18M12 3c-3 3-3 15 0 18" />
  </>),
  'Aus Equities': strokeSvg(<>
    <path d="M4 5v14h16" />
    <polyline points="7 15 11 11 14 13 19 7" />
    <polyline points="16 7 19 7 19 10" />
  </>),
  'Aus REITs': strokeSvg(<>
    <rect x="5" y="3" width="14" height="18" rx="1" />
    <path d="M9 7h.01M13 7h.01M9 11h.01M13 11h.01M9 15h.01M13 15h.01" />
    <path d="M4 21h16" />
  </>),
  'AUD/USD': strokeSvg(<>
    <ellipse cx="12" cy="6" rx="7" ry="2.4" />
    <path d="M5 6v9c0 1.3 3.1 2.4 7 2.4s7-1.1 7-2.4V6" />
    <path d="M5 9c0 1.3 3.1 2.4 7 2.4s7-1.1 7-2.4" />
    <path d="M5 12c0 1.3 3.1 2.4 7 2.4s7-1.1 7-2.4" />
  </>),
  'Commodities': strokeSvg(<>
    <ellipse cx="12" cy="5" rx="7" ry="2.5" />
    <path d="M5 5v14c0 1.4 3.1 2.5 7 2.5s7-1.1 7-2.5V5" />
    <path d="M5 12c0 1.4 3.1 2.5 7 2.5s7-1.1 7-2.5" />
  </>),
  'US Bonds': strokeSvg(<>
    <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" />
    <polyline points="14 2 14 8 20 8" />
    <line x1="8" y1="13" x2="16" y2="13" />
    <line x1="8" y1="17" x2="13" y2="17" />
  </>),
  'Global REITs': strokeSvg(<>
    <rect x="5" y="3" width="14" height="18" rx="1" />
    <path d="M9 7h.01M13 7h.01M9 11h.01M13 11h.01M9 15h.01M13 15h.01" />
    <path d="M4 21h16" />
  </>),
  'Infrastructure': strokeSvg(<>
    <path d="M12 3L5 21M12 3l7 18" />
    <path d="M8.5 12h7M6.8 17h10.4" />
    <path d="M4 21h16" />
  </>),
  'Aus Bonds': strokeSvg(<>
    <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" />
    <polyline points="14 2 14 8 20 8" />
    <line x1="8" y1="13" x2="16" y2="13" />
    <line x1="8" y1="17" x2="13" y2="17" />
  </>),
  'IG Credit': strokeSvg(<>
    <path d="M12 3l7 3v5c0 4.8-3.2 7.9-7 10-3.8-2.1-7-5.2-7-10V6z" />
    <path d="M9 12l2 2 4-4" />
  </>),
};

// ---- Icons from 2026-09 onwards: SVG Repo line set (CC0), 48x48 grid, stroked -
// Source: https://www.svgrepo.com/svg/<id>/<name> (ids beside each entry). The
// supplied files style every element through a CSS class (fill none, black
// 1-unit round stroke); that is flattened here to plain attributes with
// stroke = currentColor so the colour is set in CSS (.ip-mir-v2), and the
// stroke width lifted to 1.5 so the hairline stays legible at tile size.
const lineSvg = (children) => (
  <svg width="48" height="48" viewBox="0 0 48 48" fill="none" stroke="currentColor"
    strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">{children}</svg>
);

const LINE_ICONS = {
  // 516084 ecosia
  globe: lineSvg(<>
    <path d="M8.09,21.2a29.6414,29.6414,0,0,0,2.9252,2.9252L13.66,26.16l-.407,1.3227-1.2209,1.933L9.7453,31.754" />
    <path d="M39.4077,29.2373,38.57,26.38l-.6952-2.0512-1.6279.5087-.5087,2.1366-1.4243-2.6453L33.093,22.3955l-1.4244,1.2209-1.5261,1.0174v1.2209L28.0059,27.686l-.2035,2.1366L26.48,31.0435l-1.4244,2.34-1.5261.7121-.7122-1.2209-.9157-3.1539.5087-1.1192-.7122-3.4592-1.0174-.5087-1.5261.3052-1.3226-1.5261L17.6283,21.48l.9157-1.5261,1.3226-1.0174.2035-1.73,1.2209-.9157-.3053-1.6278.7122-.407,1.0175.61.61-.4069.7122-1.2209,1.6278-1.2209,2.2384.61,2.4417.9157,5.2906-.9157" />
    <path d="M19.1132,8.54A1.353,1.353,0,0,1,20.24,10.153a1.4478,1.4478,0,0,1-.1694.5422l-2.6453.7122-1.3226.61-.4552-1.73" />
    <circle cx="24" cy="24" r="21.5" />
    <circle cx="24" cy="24" r="16.5" />
  </>),
  // 516683 google-analytics
  ausEquities: lineSvg(<>
    <path d="m37.5,42.5h0c2.7614,0,5-2.2386,5-5V10.5c0-2.7614-2.2386-5-5-5h0c-2.7614,0-5,2.2386-5,5v27c0,2.7614,2.2386,5,5,5Z" />
    <path d="m24,42.5h0c2.7614,0,5-2.2386,5-5v-16c0-2.7614-2.2386-5-5-5h0c-2.7614,0-5,2.2386-5,5v16c0,2.7614,2.2386,5,5,5Z" />
    <circle cx="10.5" cy="37.5" r="5" />
  </>),
  // 517045 indoormap
  reits: lineSvg(<>
    <polygon points="40.872 18.607 24 6.436 7.128 18.607 7.128 41.564 40.872 41.564 40.872 18.607" />
    <line x1="4.5" y1="20.5029" x2="7.1276" y2="18.6073" />
    <line x1="43.5" y1="20.5029" x2="40.8724" y2="18.6073" />
    <rect x="20.9293" y="30.479" width="6.1413" height="11.0853" />
    <rect x="30.9816" y="30.479" width="6.1413" height="6.1413" />
    <polyline points="34.249 13.829 34.249 9.104 27.698 9.104" />
    <rect x="10.8771" y="30.479" width="6.1413" height="6.1413" />
  </>),
  // 516103 elektrichki
  infra: lineSvg(<>
    <path d="M13.4555,7.8789h21.089a2.4777,2.4777,0,0,1,2.4822,2.4734V34.5685a2.4778,2.4778,0,0,1-2.4714,2.4842h-21.1a2.4778,2.4778,0,0,1-2.4822-2.4734V10.3631a2.4778,2.4778,0,0,1,2.4714-2.4842Z" />
    <path d="M16.0712,40.28H31.9288M14.2712,43.5l3.6037-6.4473m12.25,0L33.7288,43.5" />
    <rect x="14.0103" y="7.8789" width="19.9794" height="15.4643" />
    <circle cx="16.0712" cy="32.462" r="2" />
    <circle cx="31.9288" cy="32.462" r="2" />
    <path d="M16.5212,4.5H31.4788" />
    <path d="M27.3622,4.5V7.8789" />
    <path d="M20.6378,4.5V7.8789" />
  </>),
  // 516003 drm-info
  bonds: lineSvg(<>
    <path d="M10.1709,19.2607H37.8291A1.8993,1.8993,0,0,1,39.7284,21.16V41.6007A1.8993,1.8993,0,0,1,37.8291,43.5H10.1709a1.8994,1.8994,0,0,1-1.8994-1.8994V21.16A1.8993,1.8993,0,0,1,10.1709,19.2607Z" />
    <path d="M13.59,19.2607v-4.36a10.4013,10.4013,0,0,1,20.8025,0v4.36" />
    <circle cx="23.991" cy="31.3803" r="4.0339" />
  </>),
  // 516936 huawei-gameassistant
  credit: lineSvg(<>
    <path d="M6.1467,41.5748,6.2789,28.36l3.9578.0371c-.1146,5.7956,6.7986,4.9823,6.787.0729l3.3726.0313-.0469,4.6814c4.2316-1.498,4.9506,5.3472-.0387,3.8768l-.0466,4.657Z" />
    <path d="M36.9063,42.5,23.69,42.2573l-.0031-3.7064c5.7965.0525,4.9037-6.8516-.0048-6.7873l-.0028-3.3728,4.6816-.0036c-1.5433-4.2152,5.2937-5.0076,3.877-.0031l4.6573-.0035Z" />
    <path d="M41.8533,12.8944,34.9317,24.1526,31.5227,21.96c3.084-4.9082-3.1883-7.5287-5.7075-3.315l-2.9105-1.8863,2.452-3.9882c-4.3985-.8961-1.4884-7.1334,2.03-3.3027L29.8263,5.5Z" />
    <path d="M7.0054,10.135H20.2216v3.958c-5.7965-.0566-4.909,6.8481,0,6.7874v3.3728H15.54c1.54,4.2164-5.2973,5.0038-3.8769,0H7.0057Z" />
  </>),
  // 516303 fing
  commodities: lineSvg(<>
    <circle cx="24" cy="24" r="4.5" />
    <circle cx="24" cy="6" r="3.5" />
    <circle cx="24" cy="42" r="3.5" />
    <circle cx="11.2721" cy="36.7279" r="3.5" />
    <circle cx="11.2721" cy="11.2721" r="3.5" />
    <circle cx="36.7279" cy="36.7279" r="3.5" />
    <circle cx="6" cy="24" r="3.5" />
    <path d="M27.4841,41.9178a18.1473,18.1473,0,0,0,6.7212-2.7857" />
    <path d="M13.7947,39.1321a18.1473,18.1473,0,0,0,6.7212,2.7857" />
    <path d="M6.085,27.4988a18.149,18.149,0,0,0,2.7829,6.7065" />
    <path d="M8.8593,13.8074A18.1478,18.1478,0,0,0,6.085,20.5012" />
    <path d="M20.5,6.0853A18.1479,18.1479,0,0,0,13.8069,8.86" />
    <path d="M39.6141,14.547A18.2693,18.2693,0,0,0,27.4986,6.085" />
    <path d="M39.1321,34.2053A18.1646,18.1646,0,0,0,42.25,24a18.4549,18.4549,0,0,0-.1186-2.092" />
    <polyline points="40.165 9.577 39.614 14.547 34.645 13.996" />
  </>),
  // 516198 expense-register
  aud: lineSvg(<>
    <path d="M16.5169,14.3442l7.7047-4.801,10.2741,8.6883v12.5665l-5.9671,4.836v-11.8175l-12.0117-9.4722Z" />
    <path d="M26.0581,9.2578l5.8416-3.6121,10.4601,7.293-6.4328,4.9258" />
    <path d="M36.2041,28.6126l6.2959-5.1397" />
    <path d="M36.2041,25.9523l6.2959-5.1397" />
    <path d="M36.2041,23.292l6.2959-5.1397" />
    <path d="M36.2041,20.6317l6.2959-5.1397" />
    <path d="M35.3139,14.172l2.7236-2.077-1.865-1.2474-1.4987,1.1314" />
    <path d="M5.5,31.9538l13.5429,10.4006,7.4233-5.9106" />
    <path d="M5.5,29.2851l13.5429,10.4006,7.4233-5.9106" />
    <path d="M5.6039,26.6164l13.5429,10.4006,7.4233-5.9106" />
    <path d="M5.5892,23.9478l13.5429,10.4006,7.4233-5.9106" />
    <path d="M20.2345,23.7501c-.226,1.0274-1.6933,1.5535-3.2773,1.1753h0c-1.5841-.3783-2.685-1.5178-2.459-2.5451,.226-1.0274,1.6933-1.5535,3.2773-1.1753s2.685,1.5177,2.459,2.5451Z" />
    <path d="M15.0514,15.826l-9.2955,5.5946,13.3311,10.1174,7.6392-6.0147" />
  </>),
};

const ICONS_2026_09 = {
  'Global Equities': LINE_ICONS.globe,
  'Aus Equities': LINE_ICONS.ausEquities,
  'Global REITs': LINE_ICONS.reits,
  'Aus REITs': LINE_ICONS.reits,
  'Infrastructure': LINE_ICONS.infra,
  'Aus Bonds': LINE_ICONS.bonds,
  'US Bonds': LINE_ICONS.bonds,
  'IG Credit': LINE_ICONS.credit,
  'Commodities': LINE_ICONS.commodities,
  'AUD/USD': LINE_ICONS.aud,
};

// `period` is the report month ('YYYY-MM'); a missing period gets the current look.
const isNewStyle = (period) => !(typeof period === 'string' && period < NEW_STYLE_FROM);

// Month-in-Review headline stat tiles (values are fractions). `period` is the
// report month ('YYYY-MM') and selects which icon set is drawn.
export default function IpTiles({ tiles, period }) {
  const items = tiles || [];
  if (!items.length) return <div className="chart-placeholder">No data available</div>;
  const v2 = isNewStyle(period);
  const icons = v2 ? ICONS_2026_09 : LEGACY_ICONS;
  return (
    <div className={`ip-mir-tiles${v2 ? ' ip-mir-v2' : ''}`}>
      {items.map((t, i) => {
        const v = t.value;
        const cls = v == null ? '' : v >= 0 ? 'pos' : 'neg';
        const icon = icons[t.label];
        return (
          <div key={i} className="ip-mir-tile">
            {icon && <div className="ip-mir-icon">{icon}</div>}
            <div className="ip-mir-text">
              <div className="ip-mir-label">{t.label}</div>
              <div className={`ip-mir-value ${cls}`}>{fmtPct(v)}</div>
            </div>
          </div>
        );
      })}
    </div>
  );
}
